import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { SubmitHandler } from "react-hook-form";
import { TacticalBoardEditor } from "@/features/canvas/TacticalBoardEditor";
import { TelestratorCanvas } from "@/features/canvas/TelestratorCanvas";
import {
  emptyScene,
  sceneFromOnDisk,
  sceneToOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import {
  emptyDrawing,
  loadDrawing,
  saveDrawing,
  type DrawingV2,
} from "@/lib/canvas/telestrator-schema";
import { ApiError, errorMessage, getAuthToken } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type {
  CoachingNote,
  CreateCoachingClipRequest,
  CreateCoachingNoteRequest,
  Match,
  UpdateCoachingClipRequest,
  UpdateCoachingNoteRequest,
} from "@/lib/types";
import {
  Composer,
  coerceNoteCategory,
  parseTagsRaw,
  type ComposerHandle,
  type ComposerValues,
} from "./components/Composer";
import {
  ClipComposer,
  MAX_CLIP_DURATION_SECONDS,
  type ClipComposerHandle,
  type ClipComposerValues,
} from "./components/ClipComposer";
import {
  FloatingToolPanel,
  FloatingToolToggle,
} from "./components/FloatingToolOverlay";
import { MatchSlotPicker, matchLabel } from "./components/MatchSlotPicker";
import { SourceToggle, type CoachReviewSource } from "./components/SourceToggle";
import { TemplateSelector } from "./components/TemplateSelector";
import { ToolRail } from "./components/ToolRail";
import { ClipTrack } from "./components/ClipTrack";
import { ClipPresetBar } from "./components/ClipPresetBar";
import { ThumbnailStrip } from "./components/ThumbnailStrip";
import { FrameStepper } from "./components/FrameStepper";
import { NotesAndClipsRail } from "./components/NotesAndClipsRail";
import { ComposerDrawer } from "./components/ComposerDrawer";
import { FilmIcon, PlusIcon } from "./components/icons";
import { clamp, FRAME, type ClipWindow } from "./clipTrack";
import { useCoachReviewBundle } from "./hooks/useCoachReviewBundle";
import { useCoachNotes } from "./hooks/useCoachNotes";
import { useCoachClips } from "./hooks/useCoachClips";
import { useReviewKeyboardShortcuts } from "./hooks/useReviewKeyboardShortcuts";
import { useSaveCoachingNote } from "./hooks/useSaveCoachingNote";
import { useSaveCoachingClip } from "./hooks/useSaveCoachingClip";
import { useMatches } from "@/features/matches/useMatches";
import { useMe } from "@/features/auth/useMe";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import {
  formatPlayhead,
  useVideoPlayhead,
} from "@/features/playback/useVideoPlayhead";
import { useVideoKeyboardShortcuts } from "@/features/playback/useVideoKeyboardShortcuts";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { CoachTabStrip } from "@/features/coach/CoachTabStrip";
import { TeamScopeBar } from "@/features/navigation/TeamScopeBar";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach Review — the unified shell for authoring video notes, video
 * clips, and tactical observations.
 *
 * Layout (M4) — mirrors the legacy Replay coach-review:
 *   - Header: title + bundle context + source toggle + focus-mode
 *     button + help button
 *   - Two columns at lg+: the LEFT column is the canvas stage
 *     (TacticalBoardEditor OR TelestratorCanvas, mode-switched by
 *     the source toggle); the RIGHT column is a single rail that
 *     stacks the canvas's built-in tool grid / color / stroke /
 *     formation controls AND, below them (passed via the canvas's
 *     `toolbarChildren` slot), the template selector + composer.
 *     The coach can type notes while watching the video / pitch.
 *   - Both surfaces get the SAME stage width (they differ only in
 *     height by aspect ratio) so the rail stays put on flip.
 *   - Focus mode (FocusModeOverlay below): the active canvas
 *     fills the viewport in a portaled overlay at <body>. The
 *     composer is hidden; tools are revealed on demand via a
 *     `<FloatingToolOverlay>` pill in the top-right corner.
 *     Mirrors legacy Replay focus-mode UX. Portal escape is
 *     load-bearing for the bounded stacking context the parent
 *     route's animation CSS creates.
 *   - Auth: M4 carries a localStorage Bearer-token placeholder.
 *     When the bundle 401s (or no token is stored), an inline
 *     `<InlineLoginPanel>` replaces the shell. Proper login UI
 *     lands in M9.
 *
 * Keyboard shortcuts: bound globally via useReviewKeyboardShortcuts.
 * Body-attribute scoping (SourceToggle sets/clears
 * `data-coach-review-source`) ensures shortcuts only fire when
 * Coach Review is the active route AND focus isn't in a form field.
 *
 * Save flow:
 *   - Composer submits its structured-text fields via onSubmit.
 *   - This component merges:
 *       - the form values (Zod-validated)
 *       - the canvas state (tactical_board_json OR drawing,
 *         depending on source)
 *       - the bundle's match context (match_id + null slot/
 *         timestamp for SP6; M5 wires HLS to provide the
 *         real timestamp_seconds)
 *   - Dispatches to useSaveCoachingNote (create OR update).
 *   - On success: invalidate the scoped cache (the hook does this)
 *     + reset the composer to fresh defaults (the SP5 key-bump
 *     pattern + clearing the template's lastAppliedId).
 *
 * Edit mode: when `note_id` is in the URL, the bundle returns the
 * note + auto-resolves its match. We pre-fill the composer +
 * canvas state from the seed and switch the save path to PATCH.
 */

export function CoachReview() {
  const search = useSearch({ from: "/coach/review" });
  // Route navigation — opening a clip from the NotesAndClipsRail
  // pushes `?clip_id=` so the bundle re-seeds in clip-edit mode.
  const navigate = useNavigate({ from: "/coach/review" });
  // Reconcile the auth + scope stores from `/api/me`. This route can
  // be navigated to directly (deep link, refresh, or straight from
  // login), and the scope store is NOT persisted — without this the
  // store's `teamId` stays null, `useMatches` (gated on `!!teamId`)
  // never fires, and the match picker is empty. `useMe` is a cached
  // query keyed `["me"]`, so calling it here dedupes with any other
  // caller — no extra round-trip.
  useMe();
  // Multi-team users must pick a team before the scope-gated review
  // surface renders — see `<TeamScopeGate>`.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const bundle = useCoachReviewBundle({
    matchId: search.match_id,
    noteId: search.note_id,
    clipId: search.clip_id,
  });
  const saveNote = useSaveCoachingNote();
  const saveClip = useSaveCoachingClip();
  const toast = useToast();
  // Team-wide match list for the video-mode match picker (across
  // all the team's seasons — tktic#8).
  const matchesQuery = useMatches();

  // ---- Local canvas state ----
  // `source` seeds from the route's `?source=` intent (the M6a
  // creation-routing carry-in — a list page's "+ New observation"
  // routes here with `?source=tactical_board`). Absent ⇒ video,
  // the default authoring surface. Editing an existing note can
  // still auto-flip the source from the bundle (see the seed
  // effect below); the route intent is just the initial value.
  const [source, setSource] = useState<CoachReviewSource>(
    search.source ?? "video",
  );
  const [scene, setScene] = useState<BoardScene>(() => emptyScene());
  const [drawing, setDrawing] = useState<DrawingV2>(() => emptyDrawing());

  // ---- Video-mode match + slot anchor ----
  // A video coaching note is anchored to a match + slot (+ timestamp).
  // `matchId` seeds from the route's `?match_id=` when present (the
  // bundle resolved it); otherwise the coach picks one. `slot`
  // defaults to "full".
  const [matchId, setMatchId] = useState<string>(search.match_id ?? "");
  const [slot, setSlot] = useState<string>("full");
  // Inline validation error when a video-mode save is attempted
  // without a match picked. Cleared on the next valid save.
  const [matchError, setMatchError] = useState<string | null>(null);
  // Set when the VOD heartbeat gets a 403 — the stream was killed
  // by an admin. The telestrator pauses the video itself; this
  // surfaces the "why" to the coach. Cleared when they pick a
  // different match (a fresh stream).
  const [streamKilled, setStreamKilled] = useState(false);
  // Keep matchId in sync if the route param changes (e.g. deep link).
  useEffect(() => {
    if (search.match_id) setMatchId(search.match_id);
  }, [search.match_id]);

  // The kill banner is keyed to the *current* match+slot — a fresh
  // stream gets a clean slate. The picker's `onMatchIdChange` also
  // clears it eagerly, but this effect covers the programmatic
  // paths the picker handler misses (a timeline-chip seek that
  // switches match, a deep-link route change). If the NEW match's
  // heartbeat also 403s, `useVodHeartbeat` re-raises the banner —
  // that's correct: a different killed stream is a new notice.
  useEffect(() => {
    setStreamKilled(false);
  }, [matchId, slot]);

  // ---- Composer mode: note vs. clip ----
  // Video mode hosts two composers — a coaching-NOTE composer and a
  // CLIP composer (a `[start, end]` window of the match video).
  // `composerMode` swaps which one the rail renders. Editing a clip
  // (`?clip_id=`) forces clip mode; editing a note (`?note_id=`)
  // forces note mode; otherwise the coach toggles. Tactical-board
  // mode has no clips — observations are notes only — so the toggle
  // is video-only and clip mode never applies there.
  const isClipEdit = search.clip_id !== undefined;
  const isNoteEdit = search.note_id !== undefined;
  const [composerMode, setComposerMode] = useState<"note" | "clip">(
    isClipEdit ? "clip" : "note",
  );
  // Keep the mode pinned to whichever edit the route demands.
  //
  // This runs on every `source` change too — not just on a route
  // change — because clip-edit must STAY clip mode. The tactical-
  // board source forces `note` mode (the board has no clips), and
  // the SourceToggle stays enabled under `?clip_id=`, so without
  // re-pinning a coach could go video → tactical-board → video and
  // land on the NOTE composer under a clip-edit URL — Save would
  // then write note data instead of updating the clip. Re-pinning
  // whenever the source is back on `video` closes that escape.
  useEffect(() => {
    if (source === "tactical_board") {
      // Observations are notes only — the board has no clip mode.
      setComposerMode("note");
      return;
    }
    // Video surface: the route param wins. Clip-edit pins clip
    // mode (including after a tactical-board round-trip);
    // note-edit pins note mode. A non-edit session leaves the
    // coach's manual toggle alone.
    if (isClipEdit) setComposerMode("clip");
    else if (isNoteEdit) setComposerMode("note");
  }, [isClipEdit, isNoteEdit, source]);

  // The full Match row for the picked id — needed by the
  // telestrator's `<HlsVideo>` (which builds stream URLs from
  // `match.team_id`). Resolve from the team-wide matches list
  // first; fall back to the bundle's resolved match (edit mode
  // hydrates the note's match there before the list lands).
  const selectedMatch = useMemo(
    () =>
      matchesQuery.data?.find((m) => m.id === matchId) ??
      bundle.data?.match ??
      undefined,
    [matchesQuery.data, matchId, bundle.data?.match],
  );

  // ---- Stage size: single container-width budget ----
  //
  // `containerWidth` is the available width the canvas stage should
  // fill — the measured width of the stage `<section>`.
  //
  // The section is marked `overflow-hidden` (see the JSX): the
  // canvas stage renders as a fixed-px, `flex-shrink-0` box at
  // `width={stageWidth}`, and without the clip an oversized stage
  // would inflate the section's own box, latching `containerWidth`
  // onto the largest size ever seen (a feedback loop that never
  // shrinks on resize). With `overflow-hidden` the section's box
  // stays exactly its flex-allocated width regardless of the
  // child, so observing it directly is stable.
  //
  // A CALLBACK ref (not `useRef` + a one-shot `useEffect`): the
  // section renders conditionally (behind the auth gate /
  // `selectionRequired` early-return). A `[]`-dep effect reading
  // `ref.current` would run once, find `null` if the section isn't
  // mounted yet, and never retry. The callback ref fires exactly
  // when the node attaches — same load-bearing pattern as
  // `useCanvasEnvelope`. `observe()` delivers an initial
  // measurement synchronously-ish, and every resize after.
  const [containerWidth, setContainerWidth] = useState(800);
  const stageMeasureObserver = useRef<ResizeObserver | null>(null);
  const stageMeasureRef = useCallback((node: HTMLElement | null) => {
    stageMeasureObserver.current?.disconnect();
    if (!node) {
      stageMeasureObserver.current = null;
      return;
    }
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.max(320, Math.floor(entry.contentRect.width)));
      }
    });
    obs.observe(node);
    stageMeasureObserver.current = obs;
  }, []);

  // ---- Focus mode ----
  // Declared early so the width budget below can read `focusOpen`.
  const [focusOpen, setFocusOpen] = useState(false);
  const focusButtonRef = useRef<HTMLButtonElement | null>(null);

  // ---- Composer drawer (v2) ----
  // The v2 layout moves the note / clip composer out of the canvas's
  // always-visible toolbar rail into a bottom slide-up drawer, opened
  // on demand by the "Mark moment" / "Make clip" stage buttons (and
  // the M / C keys / the rail's "+ New"). `composing` is the open
  // flag; the composer node mounts only while it's true (inline
  // mode) — closing unmounts it, which resets the form, matching the
  // standalone reference's "fresh form each open" behavior.
  //
  // Focus mode is unaffected: it still hosts the composer in its
  // floating tool panel (`composer={composerElement}` below). The
  // drawer is the INLINE surface only — opening focus mode closes
  // the drawer so the composer never mounts in two places at once.
  //
  // Edit mode (`?note_id=` / `?clip_id=`) opens the drawer
  // immediately — the coach navigated here specifically to edit
  // that note/clip, so the composer must be visible without a
  // click. A fresh (non-edit) mount starts with the drawer closed.
  const [composing, setComposing] = useState(
    () => isNoteEdit || isClipEdit,
  );
  const composeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Re-open the drawer when edit mode is ENTERED via in-app
  // navigation. The `composing` initializer above runs once, so a
  // direct-load `?clip_id=` / `?note_id=` URL opens the drawer — but
  // clicking a card in the notes/clips strip only updates the route
  // search, and without this effect the bundle would fetch while the
  // drawer stayed shut. Keyed on the edit IDS (not just the flags)
  // so it also fires when navigating straight from one edit target
  // to another (clip A → clip B keeps `isClipEdit` true the whole
  // time). It never CLOSES the drawer — a coach mid-compose who
  // clears the param keeps their open drawer.
  useEffect(() => {
    if (search.note_id !== undefined || search.clip_id !== undefined) {
      setComposing(true);
    }
  }, [search.note_id, search.clip_id]);

  // The stage fills the FULL width of its column.
  //
  // v1 subtracted a `CANVAS_TOOLBAR_BUDGET_PX` because the canvas
  // rendered its own 300px toolbar column inside this container. The
  // v2 restructure relocated the entire canvas toolbar into the
  // separate right-rail `<aside>` (via `toolRailSlot`), so the
  // canvas no longer has an in-container toolbar — subtracting the
  // budget would just leave a ~300px dead gap beside the video.
  //
  // `stageContainerRef`'s `<section>` is `flex-1` next to the 320px
  // aside, so `containerWidth` already IS the max width; the
  // `ResizeObserver` keeps it current, and `envelopeHeight` derives
  // 16:9 from it — so the stage stays max-width + aspect-correct on
  // every resize. Focus mode is the same (the overlay fills the
  // viewport).
  const stageWidth = useMemo(
    () => Math.max(320, containerWidth),
    [containerWidth],
  );

  // Both surfaces are now 16:9 and take the SAME width — the video
  // fills its 16:9 frame, and the tactical board is a 16:9 green
  // field with the pitch markings drawn inset at true proportions
  // (`TacticalBoardEditor` / `PitchBackground`). So a single
  // `stageWidth` drives both; no separate board sizing / letterbox.
  const videoStageWidth = stageWidth;
  const boardStageWidth = stageWidth;

  // ---- Seed scene/drawing/match anchor from bundle (edit mode) ----
  useEffect(() => {
    if (!bundle.data?.note) return;
    const note = bundle.data.note;
    if (note.tactical_board_json) {
      // `tactical_board_json` is the on-disk shape (`stroke_width`).
      // `sceneFromOnDisk` converts it to the in-memory shape
      // (`strokeWidth`) the canvas + `sceneToOnDisk` round-trip
      // expects. Fall back to an empty scene if the blob is invalid.
      const parsed = sceneFromOnDisk(note.tactical_board_json);
      setScene(parsed.ok ? parsed.value : emptyScene());
      // Auto-flip source toggle to the surface that has content
      if (note.note_context === "observation") setSource("tactical_board");
    }
    if (note.drawing && Object.keys(note.drawing).length > 0) {
      // `note.drawing` is the backend's on-disk shape (`type`-keyed).
      // `loadDrawing` converts it to the canonical in-memory shape
      // (`kind`-keyed) the canvas + `saveDrawing` round-trip expects.
      setDrawing(loadDrawing(note.drawing));
    }
    // Editing a video note → seed the match + slot anchor so the
    // picker opens on the note's existing match/slot.
    if (note.note_context === "video") {
      if (note.match_id) setMatchId(String(note.match_id));
      if (note.slot) setSlot(String(note.slot));
    }
  }, [bundle.data?.note]);

  // ---- Seed match anchor + drawing from bundle (clip edit mode) ----
  // Editing a clip (`?clip_id=`) — seed the match/slot picker on the
  // clip's anchor, and restore its drawing snapshot onto the canvas.
  // The clip's match/slot are immutable, so the picker is also
  // locked in clip-edit mode (see `MatchSlotPicker disabled` below).
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!clip) return;
    if (clip.match_id) setMatchId(String(clip.match_id));
    if (clip.slot) setSlot(String(clip.slot));
    if (clip.drawing && Object.keys(clip.drawing).length > 0) {
      // On-disk → in-memory, same as the note seed above.
      setDrawing(loadDrawing(clip.drawing));
    }
  }, [bundle.data?.clip]);

  // ---- Composer default values ----
  const composerDefaults = useMemo<Partial<ComposerValues> | undefined>(() => {
    const note = bundle.data?.note;
    if (!note) {
      // Fresh mount (no edit-mode note). The M6a creation-routing
      // carry-in passes `?player_id=` so a "+ New observation"
      // routed from a Roster row opens with that player
      // preselected. Only that one field — everything else is the
      // composer's own DEFAULTS.
      if (search.player_id) {
        return { player_ids: [search.player_id] };
      }
      return undefined;
    }
    return {
      title: note.title,
      note_type: note.note_type,
      // The API note payload types `category` as a free string;
      // narrow it to the composer's closed enum (off-list → "other",
      // matching the backend default).
      category: coerceNoteCategory(note.category),
      visibility: note.visibility,
      body: note.body ?? "",
      player_summary: note.player_summary ?? "",
      what_happened: note.what_happened ?? "",
      why_it_matters: note.why_it_matters ?? "",
      what_to_do_next: note.what_to_do_next ?? "",
      coach_private_note: note.coach_private_note ?? "",
      tags_raw: (note.tags ?? []).join(", "),
      player_ids: note.player_ids ?? [],
      // Observation event metadata — empty for video notes.
      event_title: note.event_title ?? "",
      event_date: note.event_date ?? "",
      event_type: note.event_type ?? "practice",
    };
  }, [bundle.data?.note, search.player_id]);

  // Template-apply uses a key-bump on the composer so RHF picks up
  // the new defaults via the reset effect inside Composer.
  const [composerKey, setComposerKey] = useState(0);
  const [composerOverride, setComposerOverride] = useState<
    Partial<ComposerValues> | undefined
  >(undefined);

  // Imperative handle into the composer's RHF state. Two callers
  // need the live values (not the parent's stale defaults):
  //   - TemplateSelector's overwrite-protect check
  //   - the focus-mode portal transition, which snapshots the form
  //     before remounting the Composer inside the drawer
  // See ComposerHandle docstring for the full rationale.
  const composerRef = useRef<ComposerHandle | null>(null);
  const getLiveComposerValues = (): Partial<ComposerValues> =>
    composerRef.current?.getValues() ?? composerOverride ?? composerDefaults ?? {};

  // ---- Clip composer state ----
  const clipComposerRef = useRef<ClipComposerHandle | null>(null);
  // Key-bump remounts the clip composer to re-seed defaults (after a
  // save, or on the focus-mode portal transition — see the focus
  // handlers).
  const [clipComposerKey, setClipComposerKey] = useState(0);
  // Override that survives a key-bump remount — the snapshot of
  // live clip-composer values taken before the focus-mode portal
  // transition, mirroring the note composer's `composerOverride`.
  // `undefined` means "use the computed defaults".
  const [clipComposerOverride, setClipComposerOverride] = useState<
    Partial<ClipComposerValues> | undefined
  >(undefined);
  // Frozen create-mode window. The playhead-derived `[t-5, t+8]`
  // window is captured into STATE once (on entering clip-create
  // mode, and after a save), NOT recomputed every render — a
  // per-tick memo would hand `ClipComposer` a fresh `defaultValues`
  // object on every `timeupdate`, and the composer resets its RHF
  // state whenever `defaultValues` changes, so the coach's
  // in-progress title / start / end / etc. would be wiped ~4×/s
  // during playback. `null` until clip-create mode is first
  // entered.
  const [clipCreateDefaults, setClipCreateDefaults] = useState<
    Partial<ClipComposerValues> | null
  >(null);
  // Focused feedback player modal — M5 coach-side scope. Opened from
  // the "Preview clip" / "Preview note" buttons in clip-edit /
  // note-edit mode (the bundle's seed clip / note is what's
  // previewed). Two independent flags — only one edit mode is
  // active at a time, but keeping them separate makes the render
  // condition for each modal explicit.
  const [clipPreviewOpen, setClipPreviewOpen] = useState(false);
  const [notePreviewOpen, setNotePreviewOpen] = useState(false);
  // Edit-mode clip defaults — hydrated from the bundle's seed clip.
  // Create-mode defaults are the frozen `clipCreateDefaults` state
  // above (NOT a per-render memo — see that state's comment).
  const clipEditDefaults = useMemo<
    Partial<ClipComposerValues> | undefined
  >(() => {
    const clip = bundle.data?.clip;
    if (!clip) return undefined;
    return {
      title: clip.title,
      description: clip.description ?? "",
      category: coerceNoteCategory(clip.category),
      visibility: clip.visibility,
      start_seconds: clip.start_seconds,
      end_seconds: clip.end_seconds,
      player_ids: clip.player_ids ?? [],
    };
  }, [bundle.data?.clip]);

  // Ref to the telestrator's backing `<video>` — forwarded to
  // whichever `<TelestratorCanvas>` is mounted (inline or focus
  // overlay). Drives the live time readout, the video keyboard
  // shortcuts, and — the M4 parity-audit P0-2 fix — the
  // `timestamp_seconds` a video note saves at.
  //
  // The element is tracked in STATE, not just a ref: the canvas
  // forwards it up through a callback ref deep inside
  // `<TelestratorCanvas>`, and nothing else would re-render
  // `CoachReview` when it mounts. `useVideoPlayhead` reads
  // `videoRef.current` at render time, so without a re-render it
  // would stay pinned to `null` and the readout would never leave
  // `--:--`. `setVideoEl` gives us that re-render; `videoElRef`
  // mirrors the state so the two hooks keep their `RefObject` API.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  videoElRef.current = videoEl;
  const handleVideoElRef = useCallback((el: HTMLVideoElement | null) => {
    setVideoEl(el);
  }, []);
  const playhead = useVideoPlayhead(videoElRef);
  useVideoKeyboardShortcuts({ videoRef: videoElRef, source });

  // ---- v2 clip track ----
  // The v2 layout puts a draggable IN/OUT clip track in the main
  // content column. `clipWindow` (absolute seconds) is the single
  // source of truth shared by the ClipTrack, the ClipPresetBar, the
  // ThumbnailStrip, and the ClipComposer's start/end defaults.
  //
  // It's derived from / re-seeded by the same `[t-5, t+8]` window the
  // ClipComposer uses (`clipCreateDefaults` / `clipEditDefaults`), but
  // held in its own state so a track drag updates it live without
  // churning the composer's other fields (the composer is only
  // re-seeded with the window at drawer-open time — see
  // `clipComposerKey`).
  const [clipWindow, setClipWindow] = useState<ClipWindow | null>(null);
  // Loop-within-window toggle. When on, the playhead bounces back to
  // `clipWindow.start` whenever it crosses `clipWindow.end`.
  const [loop, setLoop] = useState(false);
  // Media duration, tracked reactively off the backing `<video>` so
  // the clip track can map seconds → pixels. 0 until metadata loads.
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const el = videoEl;
    if (!el) {
      setDuration(0);
      return;
    }
    const sync = () => {
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    };
    sync();
    el.addEventListener("loadedmetadata", sync);
    el.addEventListener("durationchange", sync);
    return () => {
      el.removeEventListener("loadedmetadata", sync);
      el.removeEventListener("durationchange", sync);
    };
  }, [videoEl]);

  // Loop enforcement — bounce back to `clipWindow.start` when the
  // playhead crosses `clipWindow.end`. Distinct from
  // `useClipBoundaryWatcher` (which PAUSES at the boundary — used by
  // the feedback player); loop seeks back instead. A 250ms fallback
  // interval covers HLS `timeupdate` throttling, same as the
  // boundary watcher.
  useEffect(() => {
    const el = videoEl;
    if (!loop || !el || !clipWindow) return;
    const enforce = () => {
      if (el.currentTime >= clipWindow.end - 0.05) {
        el.currentTime = clipWindow.start;
        if (el.paused) void el.play().catch(() => undefined);
      }
    };
    el.addEventListener("timeupdate", enforce);
    const intervalId = window.setInterval(enforce, 250);
    return () => {
      el.removeEventListener("timeupdate", enforce);
      window.clearInterval(intervalId);
    };
  }, [videoEl, loop, clipWindow]);

  // Compute a FRESH clip-create window from the CURRENT playhead —
  // `[playhead − 5s, playhead + 8s]`, clamped to `[0, duration]`
  // and capped at the 120s max (ports the legacy "Save Clip"
  // pre-fill). Called only at the moments a re-seed is wanted
  // (entering clip-create mode, after a save) — NOT every render —
  // so a `timeupdate` tick can't churn the clip composer's
  // `defaultValues` and wipe in-progress edits.
  const freshClipWindow = useCallback((): Partial<ClipComposerValues> => {
    const t = playhead.currentTime ?? 0;
    const dur = videoElRef.current?.duration;
    const hardMax = Number.isFinite(dur) ? (dur as number) : Infinity;
    const start = Math.max(0, t - 5);
    const end = Math.min(
      hardMax,
      Math.min(start + MAX_CLIP_DURATION_SECONDS, t + 8),
    );
    return {
      start_seconds: Number(start.toFixed(1)),
      end_seconds: Number(end.toFixed(1)),
      visibility: "team",
      category: "other",
    };
  }, [playhead]);

  // Seed the frozen create-mode window ONCE when clip-create mode
  // is first entered (video surface, clip mode, not a clip-edit).
  // It's captured at entry time and then held — the coach's
  // subsequent edits + playback ticks don't disturb it. A save
  // re-seeds it explicitly (see `handleClipSubmit`). `clipCreate-
  // Defaults` is also reset to `null` when clip-create mode is
  // left, so the NEXT entry takes a fresh window from wherever the
  // playhead is then, rather than a stale one from a prior session.
  const inClipCreateMode =
    source === "video" && composerMode === "clip" && !isClipEdit;
  useEffect(() => {
    if (inClipCreateMode) {
      setClipCreateDefaults((prev) => prev ?? freshClipWindow());
      // Seed the v2 clip track's window from the same `[t-5, t+8]`
      // window — once, on entry. A drag then owns it.
      setClipWindow((prev) => {
        if (prev) return prev;
        const w = freshClipWindow();
        return {
          start: w.start_seconds ?? 0,
          end: w.end_seconds ?? 0,
        };
      });
    } else {
      setClipCreateDefaults(null);
      // Leaving clip-create mode drops the window + loop so the next
      // entry starts clean.
      setClipWindow(null);
      setLoop(false);
    }
    // `freshClipWindow` changes identity with the playhead — but
    // it's only CALLED inside the `prev ?? …` guard, so once the
    // window is frozen a playhead tick re-running this effect is a
    // no-op (`prev` is non-null). Intentional: the effect re-runs,
    // the state doesn't change, no churn.
  }, [inClipCreateMode, freshClipWindow]);

  // Clip-EDIT mode — seed the track window from the bundle's seed
  // clip (its `start_seconds` / `end_seconds`). Distinct path from
  // create mode: edit windows come from the persisted clip, not the
  // playhead.
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!isClipEdit || !clip) return;
    setClipWindow({
      start: clip.start_seconds,
      end: clip.end_seconds,
    });
  }, [isClipEdit, bundle.data?.clip]);

  // ---- Timeline chip rail (M4 audit → M5) ----
  // The rail under the video shows the selected match's video
  // notes. `useCoachNotes` is gated on `matchId` — no match, no
  // fetch (the rail renders its own no-match state). `activeNoteId`
  // tracks the last-seeked chip; it also feeds the Save Clip
  // `source_note_id` derivation in a later slice.
  const coachNotesQuery = useCoachNotes(matchId || undefined);
  const railNotes = coachNotesQuery.data?.notes ?? [];
  // The selected match's clips — feeds the clips rail + the
  // clip-track existing-clips lane. Same scope/match gating as
  // `useCoachNotes`; an `invalidateScope` from a clip save refetches.
  const coachClipsQuery = useCoachClips(matchId || undefined);
  const [activeNoteId, setActiveNoteId] = useState<number | null>(null);

  // Click a chip → jump the video to that note's moment and
  // restore its drawing snapshot onto the canvas. Ports legacy
  // `seekCoachReviewNote`. When the note belongs to a different
  // match/slot than what's loaded, switch the picker first — the
  // `<HlsVideo>` remounts on the match change and the seek lands
  // once the new source loads (the `seeked`/`loadedmetadata`
  // subscription in `useVideoPlayhead` keeps the readout honest).
  const handleSeekNote = useCallback(
    (note: CoachingNote) => {
      setActiveNoteId(note.id);
      const target = Math.max(0, Number(note.timestamp_seconds ?? 0));
      const sameAnchor = note.match_id === matchId && note.slot === slot;
      if (!sameAnchor) {
        if (note.match_id) setMatchId(note.match_id);
        if (note.slot) setSlot(note.slot);
      }
      // Restore the note's drawing snapshot (or clear to empty).
      // `note.drawing` is the on-disk shape — `loadDrawing` converts
      // it to the in-memory shape the canvas expects.
      const nextDrawing =
        note.drawing && Object.keys(note.drawing).length > 0
          ? loadDrawing(note.drawing)
          : emptyDrawing();
      setDrawing(nextDrawing);
      // Seek the live element when it's already the right source;
      // on a match switch the element remounts, so a one-shot
      // `loadedmetadata` handler does the seek instead.
      const video = videoElRef.current;
      if (sameAnchor && video) {
        video.currentTime = target;
        return;
      }
      // Cross-match: wait for the freshly-mounted element to be
      // ready, then seek once.
      const pollId = window.setInterval(() => {
        const v = videoElRef.current;
        if (v && Number.isFinite(v.duration)) {
          v.currentTime = Math.min(
            Number.isFinite(v.duration) ? v.duration : target,
            target,
          );
          window.clearInterval(pollId);
        }
      }, 100);
      // Give up after 5s so a missing video file doesn't leave the
      // interval running forever.
      window.setTimeout(() => window.clearInterval(pollId), 5_000);
    },
    [matchId, slot],
  );

  // ---- v2 clip-track handlers ----
  // Seek the backing `<video>` to an absolute time. The clip track,
  // preset bar, thumbnail strip, and frame stepper all route through
  // this so there's one seek path; `useVideoPlayhead` re-renders the
  // readouts off the resulting `seeked` event.
  const seekVideo = useCallback((seconds: number) => {
    const v = videoElRef.current;
    if (!v) return;
    const dur = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = clamp(seconds, 0, dur);
  }, []);

  // Apply a clip window from a drag / preset, clamped to the media.
  const applyClipWindow = useCallback(
    (next: ClipWindow) => {
      const dur = duration > 0 ? duration : Infinity;
      setClipWindow({
        start: clamp(next.start, 0, dur),
        end: clamp(next.end, 0, dur),
      });
    },
    [duration],
  );

  // Set the clip IN point at the current playhead — keeps OUT at
  // least 5 frames ahead. Shared by the `I` shortcut + the preset bar.
  const setClipIn = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    setClipWindow((prev) => {
      const end = prev ? Math.max(prev.end, t + FRAME * 5) : t + 0.5;
      return { start: t, end };
    });
  }, [playhead]);

  // Set the clip OUT point at the current playhead — keeps IN at
  // least 5 frames behind.
  const setClipOut = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    setClipWindow((prev) => {
      const start = prev ? Math.min(prev.start, t - FRAME * 5) : Math.max(0, t - 0.5);
      return { start: Math.max(0, start), end: t };
    });
  }, [playhead]);

  const handleTemplateApply = (
    values: Partial<ComposerValues> & { __appliedTemplateId: string },
  ) => {
    const { __appliedTemplateId: _unused, ...fields } = values;
    void _unused;
    // Build the override on top of the LIVE form state so the
    // coach's unsaved edits in fields the template doesn't write
    // (visibility, tags, player_ids, coach_private_note) survive
    // the template apply. Templates only overwrite their declared
    // fields; everything else stays.
    const live = composerRef.current?.getValues();
    setComposerOverride({ ...(live ?? composerDefaults ?? {}), ...fields });
    setComposerKey((k) => k + 1);
  };

  // ---- Help popover state ----
  // (focusOpen + focusButtonRef declared earlier so width budget can
  // read focusOpen.)
  const [helpOpen, setHelpOpen] = useState(false);

  // M7.5 B7: Escape closes the popover (standard dialog behavior).
  useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen]);

  // Snapshot whichever composer is currently live into its
  // override, so it survives the focus-mode portal remount. The
  // rail hosts EITHER the note/observation composer (`composerRef`)
  // OR the clip composer (`clipComposerRef`) depending on
  // `composerMode` — in clip mode `composerRef.current` is null and
  // the live form is behind `clipComposerRef`, so a note-only
  // snapshot would silently drop the coach's clip edits across the
  // transition. Snapshot the one that's mounted.
  const snapshotActiveComposer = () => {
    if (composerMode === "clip") {
      const liveClip = clipComposerRef.current?.getValues();
      if (liveClip) setClipComposerOverride(liveClip);
    } else {
      const liveNote = composerRef.current?.getValues();
      if (liveNote) setComposerOverride(liveNote);
    }
  };

  // Open focus mode: snapshot live form values into the override so
  // when the inline composer remounts on close, it picks up any
  // edits the coach made BEFORE clicking focus. The composer node
  // is re-parented into the focus-mode floating panel while focus
  // is open (it's NOT unmounted), but the snapshot is the
  // symmetric bookend to `closeFocus` and guards the inline form's
  // continuity.
  const openFocus = () => {
    snapshotActiveComposer();
    // The drawer and focus mode both host the composer; close the
    // drawer so the form mounts only inside the focus panel.
    setComposing(false);
    setFocusOpen(true);
  };
  const closeFocus = () => {
    // Snapshot the live composer values BEFORE flipping focusOpen.
    // The composer node lives inside the focus-mode floating panel
    // while focus mode is open; closing unmounts it and remounts a
    // fresh instance inline. Without this snapshot any edits the
    // coach made inside focus mode (in EITHER composer) are lost on
    // exit.
    snapshotActiveComposer();
    setFocusOpen(false);
    // Return focus to the trigger on close. We do it after the
    // state flip so the inline composer has remounted (it's
    // unmounted while focusOpen) and the focus call lands inside
    // a regular non-inert subtree.
    queueMicrotask(() => focusButtonRef.current?.focus());
  };

  // ---- Composer drawer open / close (v2) ----
  // Opening for a note (`M` / "Mark moment" / "+ New") vs a clip
  // (`C` / "Make clip") also pins the composer mode — the drawer
  // shows whichever composer the mode selects. A route-pinned edit
  // (`?note_id=` / `?clip_id=`) keeps its forced mode; the openers
  // respect that (they don't fight `isNoteEdit` / `isClipEdit`).
  const openComposerNote = () => {
    if (!isClipEdit) setComposerMode("note");
    setComposing(true);
  };
  const openComposerClip = () => {
    // Clip mode is video-only; the tactical board has no clips.
    if (source === "video" && !isNoteEdit) setComposerMode("clip");
    setComposing(true);
  };
  const closeComposer = () => {
    setComposing(false);
    // Return focus to whichever trigger is in the stage control bar.
    queueMicrotask(() => composeButtonRef.current?.focus());
  };

  // ---- Keyboard shortcuts ----
  // The tool letters + `?` + `Esc` are handled inside the hook;
  // here we wire the v2 review-action keys to Coach Review state.
  // `C` (clip) / `M` (mark moment) open the composer drawer; `I` /
  // `O` set the clip window. On the tactical board `C` falls back to
  // a note open (no clips there). Frame step routes through the same
  // `seekVideo` the FrameStepper buttons use.
  useReviewKeyboardShortcuts({
    onToggleHelp: () => setHelpOpen((v) => !v),
    onEnterClip: () => {
      // Video → open the clip composer drawer; tactical board has no
      // clips, so `C` there just opens the note composer.
      if (source === "video") openComposerClip();
      else openComposerNote();
    },
    onMarkMoment: () => openComposerNote(),
    onSetClipIn: () => {
      if (source === "video" && composerMode === "clip") setClipIn();
    },
    onSetClipOut: () => {
      if (source === "video" && composerMode === "clip") setClipOut();
    },
    onFrameStep: (direction, shift) => {
      const v = videoElRef.current;
      if (!v) return;
      const delta = (shift ? 1 : FRAME) * direction;
      seekVideo(v.currentTime + delta);
    },
    // `L` — toggle loop. Only meaningful with a clip window; a no-op
    // otherwise (the loop effect is gated on `clipWindow`).
    onToggleLoop: () => setLoop((v) => !v),
    // `F` — toggle focus mode (the same open/close the Focus button
    // drives).
    onToggleFocus: () => {
      if (focusOpen) closeFocus();
      else openFocus();
    },
    // `V` / `B` — switch the active source surface. Mirrors the
    // SourceToggle's onChange: snapshot the live composer first so
    // the canvas remount keeps the coach's unsaved edits.
    onSwitchSource: (next) => {
      if (next === source) return;
      const live = composerRef.current?.getValues();
      if (live) setComposerOverride(live);
      setSource(next);
    },
  });

  // ---- Save handler ----
  const handleSubmit: SubmitHandler<ComposerValues> = async (values) => {
    // The note CONTEXT is driven by the active SOURCE, not by
    // whether a match happens to be in scope:
    //   - video surface          → a video coaching note
    //   - tactical_board surface → an observation note
    // (This was previously keyed off `bundle.data?.match`, which
    // meant video mode with no preloaded match silently saved an
    // observation — wrong.)
    const isObservation = source === "tactical_board";

    // Merge canvas data based on the active source.
    // tactical_board_json + drawing are SEPARATE columns on
    // coaching_notes, so a single POST/PATCH can carry both. We
    // only emit the field for the active source — the inactive
    // surface's content stays in memory but doesn't ride along
    // (an unused empty scene/drawing in the payload is harmless,
    // but skipping keeps wire traffic + the audit log small).
    // Both canvas blobs are the canonical IN-MEMORY shapes; the
    // backend stores the ON-DISK shapes. The serializers do the
    // boundary rename:
    //   - `saveDrawing`: telestrator `kind`→`type`,
    //     `strokeWidth`→`width`. Skipping it sends `kind`-keyed
    //     objects, which the backend HARD-rejects with a 422
    //     ("drawing object type must be one of …").
    //   - `sceneToOnDisk`: tactical-board `strokeWidth`→
    //     `stroke_width`. Skipping it doesn't 422 (the board
    //     validator is allowlist-style) — it SILENTLY drops the
    //     stroke width, so a thick/thin board shape saves at the
    //     default-3 fallback.
    const canvasFields =
      source === "tactical_board"
        ? { tactical_board_json: sceneToOnDisk(scene) }
        : { drawing: saveDrawing(drawing) as unknown as Record<string, unknown> };

    // Video notes need a match anchor. Block the save with a
    // visible error rather than letting the backend 422.
    if (!isObservation && !matchId) {
      setMatchError("Pick a match before saving a video note.");
      return;
    }
    setMatchError(null);

    if (search.note_id) {
      // PATCH path. `UpdateCoachingNoteRequest` accepts the
      // source/anchor/event fields too — the backend applies only
      // what we send (`model_dump(exclude_unset=True)`) and
      // revalidates the MERGED row. So the PATCH must carry the
      // active source's context the same way POST does, or:
      //   - editing an observation's event_title/date/type silently
      //     wouldn't persist, and
      //   - switching video ↔ tactical board on an existing note
      //     would leave the stale `note_context` + anchor.
      //
      // `body` carries the composer's long-form notes field — it
      // used to be hardcoded `""` here, which silently wiped the
      // stored `body` on every edit (M4 parity-audit P0-1).
      const originalContext = bundle.data?.note?.note_context;
      const isContextFlip =
        (isObservation ? "observation" : "video") !== originalContext;

      const contextFields: Partial<UpdateCoachingNoteRequest> = isObservation
        ? {
            note_context: "observation",
            // Observation event metadata — persist the composer's
            // event fields.
            event_title: values.event_title,
            event_date: values.event_date,
            event_type: values.event_type,
            // On a video→observation FLIP, clear the match anchor.
            // The stored match_id/slot/timestamp are NOT inert:
            // `db.list_coaching_notes(match_id=...)` filters purely
            // on match_id (no note_context guard), so a converted
            // observation that kept its match_id would still show
            // up in that match's note lists/timelines. POSTed
            // observations use null anchors — a flipped one must
            // match. (A note that was always an observation never
            // had anchors, so we only send the nulls on a flip.)
            ...(isContextFlip
              ? { match_id: null, slot: null, timestamp_seconds: null }
              : {}),
          }
        : {
            note_context: "video",
            // A video note needs the match anchor. Always send
            // match_id + slot so an observation→video flip lands a
            // coherent row (the merged-state validator requires
            // all three). `timestamp_seconds` is only sent on a
            // context FLIP — for an in-place video edit we must
            // NOT clobber the note's stored timestamp with 0.
            match_id: matchId,
            slot,
            ...(isContextFlip ? { timestamp_seconds: 0 } : {}),
          };

      const body: UpdateCoachingNoteRequest = {
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        ...contextFields,
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({
          mode: "update",
          id: search.note_id,
          body,
        });
      } catch (err) {
        // Keep the form intact so the coach can retry — skip the
        // post-save reset below. The inline error block stays as
        // secondary detail; the toast is the at-a-glance signal.
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation updated" : "Note updated");
    } else {
      // POST path. Video notes carry the match/slot anchor the
      // coach picked + the `timestamp_seconds` the player is at.
      //
      // M4 parity-audit P0-2 FIX: this used to be hardcoded `0` —
      // M4 had no player to scrub against — so every M4 video note
      // was silently pinned to 0:00. M5's `<HlsVideo>` exposes the
      // playhead via `useVideoPlayhead`; the note now saves at the
      // coach's actual position. `playhead.currentTime` is `null`
      // until the video loads — if the coach saves that early we
      // fall back to `0` (a valid `ge=0` timestamp; the note lands
      // at the slot start, same as a deliberate scrub-to-start).
      //
      // Observation notes pass null match/slot/timestamp + the
      // event fields (generated-required-with-default because
      // Pydantic gives them `Field(default="...")`).
      const body: CreateCoachingNoteRequest = {
        match_id: isObservation ? null : matchId,
        slot: isObservation ? null : slot,
        timestamp_seconds: isObservation ? null : (playhead.currentTime ?? 0),
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        note_context: isObservation ? "observation" : "video",
        // Observation event metadata comes from the composer's
        // event fields (rendered only in tactical-board mode). For
        // a video note these stay empty — the backend ignores
        // event_* when `note_context === "video"`.
        event_title: isObservation ? values.event_title : "",
        event_date: isObservation ? values.event_date : "",
        event_type: isObservation ? values.event_type : "",
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation saved" : "Note saved");
    }

    // M7.5 B8: per-moment sticky fields. Legacy
    // (`js/coaching/review.js:2566-2595`) clears only the
    // per-moment subset on save and keeps `category` /
    // `visibility` / `note_type` / `player_ids` sticky so a
    // coach reviewing one player can save several notes in a row
    // without re-tagging. Mirror that here: read the live form,
    // reset to DEFAULTS but with the sticky fields preserved.
    setComposerOverride(undefined);
    const live = composerRef.current?.getValues();
    if (live) {
      composerRef.current?.reset({
        // Sticky: keep these across the save.
        note_type: live.note_type,
        category: live.category,
        visibility: live.visibility,
        player_ids: live.player_ids,
        // Per-moment: everything else falls back to DEFAULTS via the
        // composer's `reset({ ...DEFAULTS, ...overrides })` merge.
      });
    } else {
      // No live values (composer not mounted) — fall back to a
      // full remount.
      setComposerKey((k) => k + 1);
    }

    // Reset the canvas state on the active source. Without this,
    // the next note saved from the same source silently inherits
    // the just-saved tactical board / drawing — stale evidence
    // attached to an unrelated note. We only clear the active
    // source so a coach mid-flow on the OTHER surface keeps their
    // in-progress work (a tactical sketch should survive a quick
    // video-note save, and vice versa).
    if (source === "tactical_board") {
      setScene(emptyScene());
    } else {
      setDrawing(emptyDrawing());
    }
  };

  // ---- Clip: source-note derivation ----
  // A clip's `source_note_id` is the note whose drawing snapshot the
  // clip should inherit. Ports legacy `_deriveClipSourceNoteId`:
  //   1. prefer the timeline chip the coach last clicked
  //      (`activeNoteId`) — but only if it's a VIDEO note on the
  //      same match/slot as the clip window;
  //   2. else fall back to a co-located note — a video note on the
  //      same match/slot whose `timestamp_seconds` falls inside the
  //      `[start, end]` window, the one closest to the window
  //      midpoint when several match.
  // CREATE-only — never re-derived on edit (a clip's source is
  // immutable; `UpdateCoachingClipRequest` doesn't carry the field).
  const deriveClipSourceNoteId = useCallback(
    (start: number, end: number): number | null => {
      const candidates = railNotes.filter(
        (n) =>
          n.note_context === "video" &&
          n.match_id === matchId &&
          n.slot === slot,
      );
      if (candidates.length === 0) return null;
      // (1) the active chip, if it qualifies + is in-window.
      if (activeNoteId != null) {
        const active = candidates.find((n) => n.id === activeNoteId);
        if (
          active &&
          Number(active.timestamp_seconds ?? -1) >= start &&
          Number(active.timestamp_seconds ?? -1) <= end
        ) {
          return active.id;
        }
      }
      // (2) co-located note closest to the window midpoint.
      const mid = (start + end) / 2;
      const inWindow = candidates
        .filter((n) => {
          const t = Number(n.timestamp_seconds ?? -1);
          return t >= start && t <= end;
        })
        .sort(
          (a, b) =>
            Math.abs(Number(a.timestamp_seconds ?? 0) - mid) -
            Math.abs(Number(b.timestamp_seconds ?? 0) - mid),
        );
      return inWindow[0]?.id ?? null;
    },
    [railNotes, matchId, slot, activeNoteId],
  );

  // ---- Clip save handler ----
  const handleClipSubmit: SubmitHandler<ClipComposerValues> = async (
    values,
  ) => {
    // A clip always needs a match anchor. Block the save with the
    // same inline error the note path uses rather than 422-ing.
    if (!matchId) {
      setMatchError("Pick a match before saving a clip.");
      return;
    }
    setMatchError(null);

    if (search.clip_id) {
      // PATCH path. `UpdateCoachingClipRequest` is `extra="forbid"`
      // and OMITS match_id/slot — a clip's anchor is immutable.
      // `source_note_id` is CREATE-only and not in the Update type.
      const body: UpdateCoachingClipRequest = {
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        player_ids: values.player_ids,
      };
      try {
        await saveClip.mutateAsync({
          mode: "update",
          id: search.clip_id,
          body,
        });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip updated");
    } else {
      // POST path. The clip carries the match/slot anchor + the
      // window + the derived source note + the canvas drawing
      // snapshot. `source_note_id` is sent only on create.
      const sourceNoteId = deriveClipSourceNoteId(
        values.start_seconds,
        values.end_seconds,
      );
      const body: CreateCoachingClipRequest = {
        match_id: matchId,
        slot,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        player_ids: values.player_ids,
        ...(sourceNoteId != null ? { source_note_id: sourceNoteId } : {}),
        // Serialize to the backend's on-disk shape (`type`-keyed
        // objects) — see the note-save comment above.
        drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
      };
      try {
        await saveClip.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip saved");
    }

    // Reset the clip composer + clear the canvas drawing so the next
    // clip starts fresh (same "next moment from scratch" behavior as
    // the note path).
    //   - drop the focus-mode snapshot override so it doesn't
    //     re-seed the next clip with the just-saved values,
    //   - clear the frozen create-mode window so the next
    //     clip-create entry takes a FRESH `[t-5, t+8]` window from
    //     wherever the playhead is then,
    //   - key-bump to remount the composer at those fresh defaults,
    //   - clear the canvas drawing.
    setClipComposerOverride(undefined);
    setClipCreateDefaults(null);
    setClipComposerKey((k) => k + 1);
    setDrawing(emptyDrawing());
  };

  // ---- Render ----
  const players = bundle.data?.players ?? [];
  // Composer + template selector live INSIDE the canvas's right
  // rail via `toolbarChildren`. The canvas's built-in tool grid /
  // color / stroke / formation block sit above; the composer
  // continues the rail downward. Matches the legacy Replay coach-
  // review layout (single right column = tools + composer; left
  // column = video / pitch).
  // Submit label keys off the active SOURCE (video → note,
  // tactical_board → observation), not off whether a match is
  // loaded. Edit mode shows "Update …" for whichever kind. Note-
  // specific — the clip composer owns its own button label.
  const submitLabel = saveNote.isError
    ? "Retry save"
    : source === "tactical_board"
      ? isNoteEdit
        ? "Update observation"
        : "Save observation"
      : isNoteEdit
        ? "Update note"
        : "Save note";

  // ---- v2 tool-rail relocation ----
  // The canvas's tool grid / color / stroke controls are relocated
  // out of the canvas's own column into the v2 right rail via the
  // canvas's `toolRailSlot` render-prop. The canvas hands us the
  // control block; we portal it into this rail target so it lands
  // in the rail's "Tools" section. State (not just a ref) so the
  // first portal render happens once the rail node mounts.
  const [toolRailTarget, setToolRailTarget] = useState<HTMLElement | null>(
    null,
  );

  // ---- v2 clip track block ----
  // Clip mode is active whenever the clip composer is the one in
  // play (create or edit) on the video surface. The clip track,
  // preset bar, and thumbnail strip render only then.
  const inClipMode = source === "video" && composerMode === "clip";
  // Clips on this match — the clip rail + clip-track lane data.
  // Primary source is the per-match query (`useCoachClips` →
  // `GET /api/coach/clips?match_id=`). The bundle's edit-mode seed
  // clip is unioned in so an edit target shows immediately on
  // edit-load (before the query settles) and so a clip not yet in
  // the freshest list page still renders; deduped by id, seed last
  // so the list copy wins once it lands.
  const matchClips = useMemo(() => {
    const fetched = coachClipsQuery.data?.clips ?? [];
    const seed =
      bundle.data?.clip && bundle.data.clip.match_id === matchId
        ? bundle.data.clip
        : null;
    if (!seed || fetched.some((c) => c.id === seed.id)) return fetched;
    return [...fetched, seed];
  }, [coachClipsQuery.data?.clips, bundle.data?.clip, matchId]);
  // The clip track + transport + preset bar + thumbnail strip,
  // rendered in the main content column below the video stage.
  const clipTrackBlock = (
    <div className="flex flex-col gap-3" data-testid="coach-review-clip-block">
      {/* Transport — frame stepper + live time readout, plus the
       * composer-drawer trigger buttons (the v2 layout opens the
       * note / clip composer on demand, not always-visible). */}
      <div className="flex items-center gap-3">
        <FrameStepper videoRef={videoElRef} />
        <span className="font-mono text-[11px] text-ink-secondary tabular-nums">
          {playhead.formatted} / {formatPlayhead(duration || null)}
        </span>
        {/* Drawer triggers — hidden while a route-pinned edit is
         * open (the drawer is already shown for the edit) and while
         * the drawer itself is open. */}
        {!isNoteEdit && !isClipEdit && !composing ? (
          <div className="ml-auto flex items-center gap-2">
            <button
              ref={composeButtonRef}
              type="button"
              onClick={openComposerNote}
              data-testid="coach-review-mark-moment"
              className="inline-flex items-center gap-1.5 rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field"
            >
              <PlusIcon size={14} stroke={2.5} />
              Mark moment
              <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
                M
              </kbd>
            </button>
            <button
              type="button"
              onClick={openComposerClip}
              data-testid="coach-review-make-clip"
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
            >
              <FilmIcon size={14} />
              Make clip
              <kbd className="rounded bg-accent-ink/20 px-1 py-px font-mono text-[9px]">
                C
              </kbd>
            </button>
          </div>
        ) : null}
      </div>

      {/* Quick-clip preset bar — clip mode only, and only once the
       * video has a known duration (the presets clamp to it; the
       * thumbnail strip below samples it). With a placeholder /
       * unavailable video both are meaningless, so clip mode shows
       * just the composer + the inert track. */}
      {inClipMode && duration > 0 ? (
        <ClipPresetBar
          playhead={playhead.currentTime ?? 0}
          duration={duration}
          onSetWindow={applyClipWindow}
          onSetIn={setClipIn}
          onSetOut={setClipOut}
          onClear={() => setClipWindow(null)}
        />
      ) : null}

      {/* The scrub track — playhead, note marks, draggable IN/OUT. */}
      <ClipTrack
        duration={duration}
        playhead={playhead.currentTime ?? 0}
        onSeek={seekVideo}
        clipWindow={clipWindow}
        onClipWindowChange={applyClipWindow}
        inClipMode={inClipMode}
        notes={railNotes}
        clips={matchClips}
        activeNoteId={activeNoteId}
        onJumpToNote={handleSeekNote}
        loop={loop}
        onLoopChange={setLoop}
      />

      {/* Thumbnail strip — clip mode only, around the window, and
       * only with a known duration (it samples the span). */}
      {inClipMode && clipWindow && duration > 0 ? (
        <ThumbnailStrip
          duration={duration}
          window={clipWindow}
          onSeek={seekVideo}
        />
      ) : null}
    </div>
  );

  // Timecode line for the composer drawer's header — the clip
  // `IN → OUT · DUR` range in clip mode, otherwise the playhead the
  // note will save at.
  const composerTimecodeLabel =
    composerMode === "clip" && clipWindow
      ? `${formatPlayhead(clipWindow.start)} → ${formatPlayhead(
          clipWindow.end,
        )}`
      : `Min ${playhead.formatted}`;

  // The canvas's `toolRailSlot` implementation — portals the
  // canvas's own tool controls into the v2 right rail's Tools
  // section. Returns a portal (which renders into `toolRailTarget`),
  // so nothing shows at the canvas's location. Until the rail node
  // has mounted (`toolRailTarget` null on the very first paint) it
  // returns null and the controls simply aren't shown for that one
  // frame — the rail mounts in the same commit, so the next render
  // fills it.
  const renderToolRailSlot = (toolControls: React.ReactNode) =>
    toolRailTarget
      ? createPortal(<ToolRail>{toolControls}</ToolRail>, toolRailTarget)
      : null;

  // ---- v2 right-rail match/slot picker + timestamp ----
  // Video mode anchors notes/clips to a match + slot. The picker is
  // injected into the right rail via the canvas's `railHeaderSlot`
  // — placed AFTER the tools/color/stroke block, before the canvas
  // undo/delete/clear controls (not in the drawer; the drawer is
  // the feedback form only). Observations carry no match anchor, so
  // the tactical-board surface passes nothing here.
  const railHeader =
    source === "video" ? (
      <div className="flex flex-col gap-3" data-testid="coach-review-rail-header">
        <MatchSlotPicker
          matches={matchesQuery.data ?? []}
          matchesLoading={matchesQuery.isLoading}
          matchId={matchId}
          onMatchIdChange={(id) => {
            setMatchId(id);
            if (id) setMatchError(null);
            // The admin-kill banner is cleared by the
            // `[matchId, slot]` effect — one source of truth.
          }}
          slot={slot}
          onSlotChange={setSlot}
          disabled={isClipEdit}
        />
        {/* Live timestamp readout — a new video note saves at the
         * player's current position. `--:--` until the video loads.
         * Note-create only. */}
        {!isNoteEdit && composerMode === "note" ? (
          <p
            className="text-ink-muted text-xs"
            data-testid="coach-review-timestamp-readout"
          >
            Note saves at{" "}
            <span className="font-mono text-ink">{playhead.formatted}</span>
          </p>
        ) : null}
      </div>
    ) : null;

  const composerElement = (
    <div className="flex flex-col gap-4">
      {/* The match + slot picker, the live timestamp readout, and
       * the canvas-control rail all moved OUT of this drawer into
       * the v2 right rail (see `railHeader` below). The Save Note /
       * Save Clip toggle was removed entirely — the drawer's kind is
       * fixed by which trigger opened it ("Mark moment" → note,
       * "Make clip" → clip). The drawer now holds only the feedback
       * form. `matchError` is a save-time validation message, so it
       * stays here next to the composer. */}
      {source === "video" && matchError ? (
        <p
          className="text-destructive font-mono text-xs"
          data-testid="coach-review-match-error"
        >
          {matchError}
        </p>
      ) : null}
      {composerMode === "clip" && source === "video" ? (
        <>
          <ClipComposer
            ref={clipComposerRef}
            key={clipComposerKey}
            defaultValues={
              // Precedence: a focus-mode snapshot wins (it carries
              // the coach's live edits across the portal remount);
              // then the edit-mode seed clip; then the frozen
              // create-mode window. The v2 clip track's live
              // `clipWindow` overrides start/end on top — the track
              // is the source of truth for the window, and the
              // composer drawer mounts fresh (key-bumped) each open
              // so a track drag never churns the composer's other
              // fields mid-edit.
              {
                ...(clipComposerOverride ??
                  clipEditDefaults ??
                  clipCreateDefaults ??
                  {}),
                ...(clipWindow
                  ? {
                      start_seconds: Number(clipWindow.start.toFixed(1)),
                      end_seconds: Number(clipWindow.end.toFixed(1)),
                    }
                  : {}),
              }
            }
            players={players}
            onSubmit={handleClipSubmit}
            submitLabel={
              saveClip.isError
                ? "Retry save"
                : isClipEdit
                  ? "Update clip"
                  : "Save clip"
            }
            matchLabel={
              selectedMatch ? matchLabel(selectedMatch) : "No match selected"
            }
            slot={slot}
            isEditing={isClipEdit}
            sourceNoteId={bundle.data?.clip?.source_note_id ?? null}
          />
          {/* Preview clip — opens the focused feedback player modal.
           * Only in clip-EDIT mode: the bundle's seed clip is a
           * persisted, fully-shaped clip to preview. (Create mode
           * has no saved clip yet — the coach saves first, then
           * the route flips to ?clip_id= and the button appears.) */}
          {isClipEdit && bundle.data?.clip && selectedMatch ? (
            <button
              type="button"
              onClick={() => setClipPreviewOpen(true)}
              data-testid="coach-review-preview-clip"
              className="rounded border border-rule bg-bg-card px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-surface transition-colors"
            >
              Preview clip
            </button>
          ) : null}
        </>
      ) : (
        <>
          <TemplateSelector
            getCurrentValues={getLiveComposerValues}
            onApply={handleTemplateApply}
          />
          <Composer
            ref={composerRef}
            key={composerKey}
            defaultValues={composerOverride ?? composerDefaults}
            players={players}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            observationMode={source === "tactical_board"}
            // M7.5 F: AI panel needs the active video match's id
            // for evidence. Empty for tactical-board / observation
            // composers (the panel handles `matchId === ""`).
            matchId={matchId}
          />
          {/* Preview note — opens the focused feedback player modal
           * in note mode (video + telestration replay). Only for a
           * VIDEO note being edited: the bundle's seed note is a
           * persisted, fully-shaped note with a match anchor.
           * Observations have no video to play. */}
          {isNoteEdit &&
          bundle.data?.note?.note_context === "video" &&
          selectedMatch ? (
            <button
              type="button"
              onClick={() => setNotePreviewOpen(true)}
              data-testid="coach-review-preview-note"
              className="rounded border border-rule bg-bg-card px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-surface transition-colors"
            >
              Preview note
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  const workspaceHeader = (
    <>
      {/* Constant workspace-level header — identical to the
       * `/coach` shell's, so the two routes share one chrome.
       * Coach Review is its own route, so it renders this itself
       * rather than nesting under the shell. */}
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display tracking-tight">
            Coaching Workspace
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            Manage your roster, write timestamped notes, build review
            playlists, and mark up video — all in one place.
          </p>
        </div>
        <TeamScopeBar />
      </header>

      {/* The shared coach-workspace tab strip — same as the shell's.
       * The strip's "Review" tab is the active one here. */}
      <div className="mb-4">
        <CoachTabStrip active="review" />
      </div>
    </>
  );

  if (selectionRequired) {
    return (
      <main className="min-h-screen p-6 text-ink">
        {workspaceHeader}
        <TeamScopeGate />
      </main>
    );
  }

  return (
    <main className="min-h-screen p-6 text-ink">
      {workspaceHeader}

      {/* M9a A8 — auth gate. The shell-level <CoachReviewAuthGate>
       * effect (below) handles the redirect to `/login?next=/coach/review`.
       * Nothing renders in place of the old <InlineLoginPanel> — the
       * redirect happens before the user sees a flash of empty UI. */}
      <CoachReviewAuthGate bundle={bundle} />

      {bundle.isError &&
      !(bundle.error instanceof ApiError && bundle.error.status === 401) ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-bundle-error"
        >
          Failed to load bundle:{" "}
          {bundle.error instanceof Error ? bundle.error.message : "unknown"}
        </p>
      ) : null}
      {saveNote.isError ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-save-error"
        >
          Save failed:{" "}
          {saveNote.error instanceof Error
            ? saveNote.error.message
            : "unknown"}
        </p>
      ) : null}
      {streamKilled ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-stream-killed-error"
        >
          This stream was disconnected by an administrator. Pick a
          different match to resume.
        </p>
      ) : null}

      <div
        className="flex flex-col gap-4"
        data-testid="coach-review-shell"
        hidden={
          bundle.isError && bundle.error instanceof ApiError && bundle.error.status === 401
            ? true
            : undefined
        }
      >
        {/* Stage-control row — the Video / Tactical Board source
         * toggle (scopes the stage) on the left, the surface-level
         * Focus mode + Shortcuts actions on the right. The v1
         * `<PageHeader>` that used to carry these was removed: its
         * title + description ("Coach Review · <team> · Video — …")
         * just restated the active tab + the source toggle, so it
         * was redundant. The actions it hosted live here now. */}
        <div className="flex items-center justify-between gap-4">
          <SourceToggle
            value={source}
            onChange={(next) => {
              // Source flip remounts the canvas (and therefore the
              // composer node living in its toolbar rail). Snapshot
              // live RHF state into composerOverride first so the
              // remount picks up the coach's unsaved edits.
              const live = composerRef.current?.getValues();
              if (live) setComposerOverride(live);
              setSource(next);
            }}
          />
          <div className="flex items-center gap-2">
            <button
              ref={focusButtonRef}
              type="button"
              onClick={openFocus}
              data-testid="coach-review-focus-button"
              className="bg-bg-card hover:bg-surface border border-rule text-ink rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
            >
              Focus mode
            </button>
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              aria-expanded={helpOpen}
              aria-label="Keyboard shortcuts"
              data-testid="coach-review-help-button"
              className="bg-bg-card hover:bg-surface border border-rule text-ink-muted hover:text-ink rounded w-9 h-9 font-mono transition-colors"
            >
              ?
            </button>
          </div>
        </div>

        {/* v2 two-column workspace — the stage column (flex-1) and
         * the notes/clips rail (320px). The canvas's own tool /
         * composer rail still lives inside the canvas via
         * `toolbarChildren`; this NotesAndClipsRail is the v2
         * addition that splits the note + clip lists into their own
         * consistently-styled column. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <section
          ref={stageMeasureRef}
          className="min-w-0 flex-1 overflow-hidden"
          aria-label="Stage"
          id="coach-review-source-panel"
          role="tabpanel"
          aria-labelledby={`coach-review-source-tab-${source}`}
        >
          {/* When focus mode is open, the inline stage is hidden —
           * the same canvas is mounted inside the fullscreen
           * overlay portal below. Two stage mounts would create
           * conflicting setSurface effects on the review-editor
           * store. The composer node is also rendered (via the
           * canvas's `toolbarChildren` slot below the tool grid),
           * so it stays alive inside the inline canvas — RHF state
           * survives focus-mode toggles. */}
          {!focusOpen ? (
            source === "tactical_board" ? (
              <div className="flex flex-col gap-3">
                <TacticalBoardEditor
                  scene={scene}
                  onSceneChange={setScene}
                  width={boardStageWidth}
                  toolRailSlot={renderToolRailSlot}
                />
                {/* Observation composer trigger — the tactical board
                 * has no clips, so just the one "Mark observation"
                 * button. Hidden on edit / while the drawer is open,
                 * same as the video triggers. */}
                {!isNoteEdit && !isClipEdit && !composing ? (
                  <div className="flex">
                    <button
                      ref={composeButtonRef}
                      type="button"
                      onClick={openComposerNote}
                      data-testid="coach-review-mark-observation"
                      className="inline-flex items-center gap-1.5 rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field"
                    >
                      <PlusIcon size={14} stroke={2.5} />
                      Mark observation
                      <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
                        M
                      </kbd>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <TelestratorCanvas
                  drawing={drawing}
                  onDrawingChange={setDrawing}
                  width={videoStageWidth}
                  match={selectedMatch}
                  slot={slot}
                  videoRef={handleVideoElRef}
                  onHeartbeatKilled={() => setStreamKilled(true)}
                  toolRailSlot={renderToolRailSlot}
                  railHeaderSlot={railHeader}
                />
                {/* v2 clip-track block — transport + scrub track +
                 * (clip mode) preset bar + thumbnail strip. Replaces
                 * the v1 TimelineChipRail: the note marks now live on
                 * the scrub track itself, and the right rail's
                 * NotesAndClipsRail carries the note list. */}
                {clipTrackBlock}
              </div>
            )
          ) : (
            <div
              className="rounded-lg border border-rule bg-surface/40 text-ink-muted text-sm font-mono px-4 py-3"
              data-testid="coach-review-stage-focused-placeholder"
            >
              Stage is open in focus mode. Press <kbd>Esc</kbd> or click Exit to return.
            </div>
          )}
        </section>

        {/* Tools rail — v2 right column. Tools ONLY (the canvas
         * portals its toolbar in here via `toolRailSlot`); it sizes
         * to its content. The notes/clips lists used to live here
         * too but moved to a full-width horizontal-card block below
         * the canvas — a height-locked rail squeezed them into an
         * unusable nested scroll. Hidden while focus mode owns the
         * screen. */}
        {!focusOpen ? (
          <aside
            ref={setToolRailTarget}
            className="flex w-full flex-col gap-4 rounded-md border border-rule bg-surface p-4 lg:w-80 lg:flex-shrink-0"
            aria-label="Drawing tools"
          />
        ) : null}
        </div>

        {/* Notes & Clips — full-width horizontal card rails below
         * the stage. Out of the right rail so the lists get real
         * room; each rail scrolls sideways. */}
        {!focusOpen ? (
          <NotesAndClipsRail
            notes={railNotes}
            clips={matchClips}
            activeNoteId={activeNoteId}
            onJumpToNote={handleSeekNote}
            onOpenClip={(clip) => {
              void navigate({
                to: "/coach/review",
                search: { match_id: clip.match_id, clip_id: clip.id },
              });
            }}
            onNewNote={openComposerNote}
            notesLoading={coachNotesQuery.isLoading}
            clipsLoading={coachClipsQuery.isLoading}
          />
        ) : null}
      </div>

      {/* Composer drawer — the v2 bottom slide-up that hosts the
       * note / clip composer. Mounted only while `composing` is true
       * AND focus mode is closed (focus mode hosts the composer in
       * its own floating panel — see the FocusModeOverlay below — so
       * mounting it here too would double-mount the form). */}
      {composing && !focusOpen ? (
        <ComposerDrawer
          kind={composerMode}
          timecodeLabel={composerTimecodeLabel}
          onClose={closeComposer}
        >
          {composerElement}
        </ComposerDrawer>
      ) : null}

      {focusOpen ? <FocusModeOverlay
        source={source}
        scene={scene}
        onSceneChange={setScene}
        drawing={drawing}
        onDrawingChange={setDrawing}
        match={selectedMatch}
        slot={slot}
        videoRef={handleVideoElRef}
        onHeartbeatKilled={() => setStreamKilled(true)}
        onClose={closeFocus}
        composer={composerElement}
      /> : null}

      {/* Focused feedback player modal — M5 coach-side scope.
       * Previews the clip being edited. Renders only when the
       * bundle has a seed clip + a resolved match for its stream
       * URLs. */}
      {clipPreviewOpen && bundle.data?.clip && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: bundle.data.clip }}
          match={selectedMatch}
          onClose={() => setClipPreviewOpen(false)}
        />
      ) : null}
      {notePreviewOpen && bundle.data?.note && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: bundle.data.note }}
          match={selectedMatch}
          onClose={() => setNotePreviewOpen(false)}
        />
      ) : null}

      {/* M7.5 B7: source-aware shortcuts popover — swaps content
       * per active `source`. Ports the legacy
       * `_refreshCoachShortcutsHelpForSource` switch in
       * `js/coaching/review.js:798-833`. */}
      {helpOpen ? (
        <div
          role="dialog"
          aria-label="Keyboard shortcuts"
          data-testid="coach-review-help-popover"
          data-source={source}
          className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-rule bg-surface p-4 shadow-xl"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink">
              Shortcuts · {source === "tactical_board" ? "Tactical board" : "Video"}
            </h3>
            <button
              type="button"
              onClick={() => setHelpOpen(false)}
              aria-label="Close shortcuts"
              className="text-ink-muted hover:text-ink text-xs"
            >
              ✕
            </button>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs font-mono">
            {(source === "tactical_board"
              ? TACTICAL_SHORTCUTS
              : VIDEO_SHORTCUTS
            ).map(([keys, label]) => (
              <ShortcutRow key={keys} keys={keys} label={label} />
            ))}
          </dl>
          <p className="text-xs text-ink-muted mt-3">
            Shortcuts skip while typing in a form field.
          </p>
        </div>
      ) : null}
    </main>
  );
}

// --- M7.5 B7: source-aware keyboard-shortcut tables ------------------
// Ports the legacy `_refreshCoachShortcutsHelpForSource` switch
// (`js/coaching/review.js:798-833`). Format: `[keysHtml, label]`
// pairs; `keysHtml` is a string with one or more keys separated by
// " / " (`ShortcutRow` splits + styles each).

const VIDEO_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["Space / K", "Play / pause"],
  ["J", "Back 5s"],
  ["← / →", "Back / forward 1s"],
  ["Shift+← / →", "Back / forward 10s"],
  [", / .", "Step back / forward 1 frame"],
  ["Shift+, / .", "Step back / forward 1 second"],
  ["C / M", "Clip mode / Mark moment"],
  ["I / O", "Set clip IN / OUT"],
  ["L", "Toggle loop in clip range"],
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["S / D / G", "Spotlight / Dim / Formation"],
  ["Esc", "Disarm / close"],
  ["?", "Toggle this help"],
];

const TACTICAL_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["M", "Mark observation"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["P / S / D / G", "Player / Spotlight / Dim / Formation"],
  ["Delete / Backspace", "Delete selected"],
  ["Esc", "Disarm + clear selection"],
  ["?", "Toggle this help"],
];

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <>
      <dt className="text-ink-muted">{keys}</dt>
      <dd>{label}</dd>
    </>
  );
}

/**
 * Fullscreen focus-mode overlay. Renders the active canvas with
 * `hideToolbar` so the stage fills the viewport, plus the floating
 * tool overlay that the coach can reveal on demand.
 *
 * Mirrors the legacy Replay focus-mode UX: video / pitch maxes the
 * browser, the side composer is hidden, and tools appear as an
 * overlay only when the coach clicks the Tools pill.
 *
 * Portaled to `<body>` so the parent's bounded stacking context
 * (CSS `animation` on the route component creates one) can't trap
 * the overlay's z-index.
 *
 * Esc closes. The overlay tags itself `data-coach-review-portal` so
 * any future inert sweep (not currently active here — focus mode
 * doesn't need keyboard-trap because all interactable elements are
 * inside the overlay and the canvas pointer surface is the whole
 * viewport) would skip the portal container.
 */
function FocusModeOverlay(props: {
  source: CoachReviewSource;
  scene: BoardScene;
  onSceneChange: (s: BoardScene) => void;
  drawing: DrawingV2;
  onDrawingChange: (d: DrawingV2) => void;
  /** Resolved match + slot for the telestrator's `<HlsVideo>` —
   * `undefined` when no match is selected (the canvas then shows
   * the no-video placeholder). */
  match: Match | undefined;
  slot: string;
  /** Forwarded to the telestrator — the parent's callback ref that
   * captures the backing `<video>`, so the playhead readout /
   * keyboard scrubbing / save timestamp keep working while focus
   * mode owns the canvas. */
  videoRef: (el: HTMLVideoElement | null) => void;
  /** Forwarded to the telestrator — fires when the VOD heartbeat
   * gets a 403 (admin kill). */
  onHeartbeatKilled?: () => void;
  onClose: () => void;
  /** The composer stack — rendered inside the floating tool panel
   * below the drawing tools so focus mode is a full authoring
   * surface (draw + write), not draw-only. */
  composer: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlaySize, setOverlaySize] = useState(() => ({
    width: typeof window === "undefined" ? 1200 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  }));

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setOverlaySize({
          width: Math.max(320, Math.floor(entry.contentRect.width)),
          height: Math.max(200, Math.floor(entry.contentRect.height)),
        });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Esc closes the overlay. Doesn't preventDefault — the broader
  // M4 keyboard-shortcut handler also reacts to Esc (disarms tools),
  // and both behaviors landing is fine.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  // The Tools toggle and its panel now live in the header /
  // anchored beneath the header — lift the open/close state up so
  // both stay in sync. Panel mount/unmount is preserved across
  // toggles (see FloatingToolPanel docstring).
  const [toolsOpen, setToolsOpen] = useState(false);

  // Largest 16:9 rectangle that fits the overlay (minus the header
  // chrome). Both surfaces are 16:9, so they share this width — the
  // video fills it; the tactical board is the 16:9 green field
  // (pitch markings drawn inset by `PitchBackground`).
  const CHROME_HEIGHT = 48;
  const VIDEO_ASPECT_W_OVER_H = 16 / 9;
  const availableH = Math.max(200, overlaySize.height - CHROME_HEIGHT);
  const availableW = Math.max(320, overlaySize.width);
  const stageW = Math.max(
    320,
    Math.min(availableW, Math.floor(availableH * VIDEO_ASPECT_W_OVER_H)),
  );

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[120] flex flex-col bg-black"
      data-testid="coach-review-focus-overlay"
      data-coach-review-portal="true"
      role="dialog"
      aria-modal="true"
      aria-label="Focus mode"
    >
      <header className="flex items-center justify-between px-3 py-2 bg-bg-card border-b border-rule/40">
        <span className="text-ink-muted text-[11px] font-display uppercase tracking-wider">
          Focus mode · {props.source === "tactical_board" ? "Tactical board" : "Video"}
        </span>
        <div className="flex items-center gap-2">
          <FloatingToolToggle
            surface={props.source}
            open={toolsOpen}
            onToggle={() => setToolsOpen((v) => !v)}
          />
          <button
            type="button"
            onClick={props.onClose}
            data-testid="coach-review-focus-exit"
            className="bg-bg-card hover:bg-surface border border-rule/60 text-ink-muted hover:text-ink rounded px-2.5 py-1 text-xs font-display uppercase tracking-wide transition-colors"
          >
            Exit focus
          </button>
        </div>
      </header>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {props.source === "tactical_board" ? (
          <TacticalBoardEditor
            scene={props.scene}
            onSceneChange={props.onSceneChange}
            width={stageW}
            hideToolbar
          />
        ) : (
          <TelestratorCanvas
            drawing={props.drawing}
            onDrawingChange={props.onDrawingChange}
            width={stageW}
            match={props.match}
            slot={props.slot}
            videoRef={props.videoRef}
            onHeartbeatKilled={props.onHeartbeatKilled}
            hideToolbar
          />
        )}
        <FloatingToolPanel surface={props.source} open={toolsOpen}>
          {props.composer}
        </FloatingToolPanel>
      </div>
    </div>,
    document.body,
  );
}

/** M9a A8 — auth gate inside the Coach Review shell. Watches the
 * bundle query for 401 / no-token; on either, redirects to
 * `/login?next=/coach/review`. Also gates on role: a signed-in user
 * with no coach access who deep-links here is bounced to `/hub` (the
 * role-based redirect) rather than left on a shell whose queries
 * would 403 — and rather than sent to `/login`, which would loop an
 * already-authenticated user. Renders nothing; the effect fires
 * before React paints so the user doesn't see a flash of empty
 * shell. Ref-flag prevents the effect re-firing after the URL
 * updates (TanStack Router keeps the parent mounted briefly). */
function CoachReviewAuthGate({
  bundle,
}: {
  bundle: ReturnType<typeof useCoachReviewBundle>;
}) {
  const navigate = useNavigate({ from: "/coach/review" });
  const me = useMe();
  const isAuthError =
    bundle.error instanceof ApiError && bundle.error.status === 401;
  const noToken = !getAuthToken();
  const showLogin = isAuthError || (bundle.isError && noToken);

  // `next` captures the FULL path + search (`/coach/review?match_id=
  // …&note_id=…`) at mount via a ref so a coach deep-linked to a
  // specific match / note / clip resumes exactly there after signing
  // in, instead of an empty Coach Review surface. The ref freezes
  // the value before the redirect rewrites the URL; `window.location`
  // is the same source `PublicAppHeader` uses for its `loginNext`.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/coach/review"
      : "/coach/review",
  );
  const authRedirectFired = useRef(false);

  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  // Wrong-role gate — mirrors CoachShell. Only fires for a resolved,
  // signed-in user with no coach access; anonymous users fall to the
  // auth gate above (this effect waits for `me.data?.user`).
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!me.data?.user) return;
    if (canOpenCoach) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, me.data, canOpenCoach, navigate]);

  return null;
}
